import React, { memo, useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import MoonLoader from "react-spinners/MoonLoader";
import { IconUsers, IconRefresh, IconUserPlus, IconChevronDown } from "@tabler/icons-react";
import { ServiceCenterAPI } from "../../services";
import { ServiceStatusBadge } from "../common";
import { formatPhone, formatRelativeTime } from "../../utils/formatters";
import "./Modals.css";

/**
 * Modal de fila de atendimento
 */
const ServiceQueueModal = memo(({
  show,
  onHide,
  onStartService,
}) => {
  const PAGE_SIZE = 20;
  const [queue, setQueue] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Carregar fila (reset ou append). Usa offset=queue.length quando append.
  const loadQueue = async ({ append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const offset = append ? queue.length : 0;
      const data = await ServiceCenterAPI.getServiceQueue({
        limit: PAGE_SIZE,
        offset,
      });
      const items = data.results || data || [];
      const nextTotal =
        typeof data.count === "number"
          ? data.count
          : append
          ? queue.length + items.length
          : items.length;

      // Dedup por id pra evitar duplicação se a fila mexeu entre páginas.
      setQueue((prev) =>
        append
          ? [
              ...prev,
              ...items.filter(
                (it) => !prev.some((p) => (p.id ?? p.phone) === (it.id ?? it.phone))
              ),
            ]
          : items
      );
      setTotal(nextTotal);
    } catch (error) {
      console.error("Erro ao carregar fila:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Iniciar atendimento de um item da fila
  const handleStartService = async (item) => {
    setSelectedItem(item.id);
    try {
      await onStartService(item.id); // Usa contact_id, não phone
      loadQueue(); // Recarregar fila (reset)
    } finally {
      setSelectedItem(null);
    }
  };

  const hasMore = queue.length < total;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="sc-modal sc-modal--full"
    >
      <Modal.Header closeButton>
        <Modal.Title className="sc-modal__title">
          <IconUsers size={22} />
          Fila de Atendimento
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Ações */}
        <div className="sc-modal__toolbar">
          <span className="sc-modal__count">
            {total} {total === 1 ? "contato" : "contatos"} na fila
            {total > queue.length && (
              <span className="sc-modal__count-page">
                {" "}(mostrando {queue.length})
              </span>
            )}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={loadQueue}
            disabled={isLoading}
          >
            <IconRefresh size={16} className={isLoading ? "spin" : ""} />
            Atualizar
          </Button>
        </div>

        {/* Lista da fila */}
        <div className="sc-modal__queue-list">
          {isLoading ? (
            <div className="sc-modal__loading">
              <MoonLoader size={30} color="#3b82f6" />
              <span>Carregando fila...</span>
            </div>
          ) : queue.length === 0 ? (
            <div className="sc-modal__empty">
              <span className="sc-modal__empty-icon">📋</span>
              <span>Nenhum contato na fila</span>
            </div>
          ) : (
            <>
              {queue.map((item) => (
                <div key={item.id || item.phone} className="sc-queue-item">
                  <img
                    src={item.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || item.phone)}&background=random`}
                    alt=""
                    className="sc-queue-item__avatar"
                  />

                  <div className="sc-queue-item__info">
                    <span className="sc-queue-item__name">
                      {item.name || formatPhone(item.phone)}
                    </span>
                    <span className="sc-queue-item__phone">
                      {formatPhone(item.phone)}
                    </span>
                    <span className="sc-queue-item__time">
                      Aguardando {formatRelativeTime(item.queued_at || item.last_message_at || item.created_at)}
                    </span>
                  </div>

                  <div className="sc-queue-item__actions">
                    <ServiceStatusBadge status={item.service_status || "new"} size="small" />
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleStartService(item)}
                      disabled={selectedItem === item.id}
                    >
                      {selectedItem === item.id ? (
                        <MoonLoader size={14} color="#fff" />
                      ) : (
                        <>
                          <IconUserPlus size={16} />
                          Atender
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="sc-modal__load-more">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => loadQueue({ append: true })}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <MoonLoader size={14} color="#3b82f6" />
                        Carregando...
                      </>
                    ) : (
                      <>
                        <IconChevronDown size={16} />
                        Carregar mais ({total - queue.length} restantes)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

ServiceQueueModal.displayName = "ServiceQueueModal";

export default ServiceQueueModal;
